import { intakeWhenLine } from "@roof-hawk/core";
import { useEffect, useState } from "react";

import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { supabase, unwrap } from "../../lib/supabase";

interface LeadRow {
  id: string;
  intake_slug: string | null;
  heard_how: string | null;
  lead_source: string | null;
  wants: string[] | null;
  preferred_days: string[] | null;
  preferred_times: string[] | null;
  notes: string | null;
  created_at: string;
}

interface PhotoRow {
  id: string;
  path: string;
  companycam_pushed_at: string | null;
}

/**
 * What the customer typed into the link, on the job.
 *
 * Jonathon, 2026-09-19: "make the notification be to where we can click on it
 * and see everything the customer put in there." The push opens the job, so
 * everything they put in has to be *on* the job — not in a table only the
 * database can see.
 *
 * It shows for a lead that came through a link and for nothing else: a job
 * typed in by a rep has no lead row and this renders nothing at all, rather
 * than an empty card on every job in the system.
 *
 * Their words are shown as their words. The note the database wrote says the
 * same things in a sentence; this is the readable version, and both exist for
 * the reason ADR 0054 gives — what the customer actually asked for stops being
 * recoverable from the job the moment somebody edits it.
 */
export function IntakeCard({ jobId }: { jobId: string }) {
  const [urls, setUrls] = useState<Record<string, string>>({});

  const lead = useAsync<LeadRow | null>(
    async () =>
      (unwrap(
        await supabase
          .from("leads")
          .select(
            "id,intake_slug,heard_how,lead_source,wants,preferred_days,preferred_times,notes,created_at",
          )
          .eq("job_id", jobId)
          .eq("origin", "link")
          .maybeSingle(),
      ) ?? null) as LeadRow | null,
    [jobId],
  );

  const photos = useAsync<PhotoRow[]>(
    async () =>
      (unwrap(
        await supabase
          .from("lead_photos")
          .select("id,path,companycam_pushed_at")
          .eq("job_id", jobId)
          .order("sort_order"),
      ) ?? []) as PhotoRow[],
    [jobId],
  );

  // The bucket is private, so every photograph is signed on read. An hour is
  // far longer than anybody keeps this screen open and far shorter than the
  // life of the row.
  useEffect(() => {
    const rows = photos.data ?? [];
    if (rows.length === 0) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.storage
        .from("lead-photos")
        .createSignedUrls(
          rows.map((photo) => photo.path),
          60 * 60,
        );
      if (cancelled || !data) return;
      const next: Record<string, string> = {};
      data.forEach((signed, index) => {
        const row = rows[index];
        if (row && signed.signedUrl) next[row.id] = signed.signedUrl;
      });
      setUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [photos.data]);

  if (lead.loading || !lead.data) return null;
  const row = lead.data;

  const when = intakeWhenLine(
    row.preferred_days ?? [],
    row.preferred_times ?? [],
  );
  const shots = photos.data ?? [];
  const waiting = shots.filter((p) => p.companycam_pushed_at === null).length;

  return (
    <div className="block intakecard">
      <h2>{t("What the customer sent")}</h2>
      <div className="na-hint">
        {t(
          "Through {link} on {date}. Their own words — the job above is yours to edit.",
          {
            link: row.intake_slug
              ? t("{slug}'s link", { slug: row.intake_slug })
              : t("the link"),
            date: new Date(row.created_at).toLocaleDateString(),
          },
        )}
      </div>

      {row.wants && row.wants.length > 0 ? (
        <div className="intakecard-row">
          <span>{t("Wants")}</span>
          <b>{row.wants.join(", ")}</b>
        </div>
      ) : null}

      {when !== "" ? (
        <div className="intakecard-row">
          <span>{t("Free")}</span>
          <b>{when}</b>
        </div>
      ) : null}

      {row.heard_how ? (
        <div className="intakecard-row">
          <span>{t("Heard about us")}</span>
          <b>
            &ldquo;{row.heard_how}&rdquo;
            {row.lead_source ? (
              <em className="na-hint">
                {" "}
                {t("— filed as {source}", { source: row.lead_source })}
              </em>
            ) : null}
          </b>
        </div>
      ) : null}

      {row.notes ? (
        <blockquote className="intakecard-note">{row.notes}</blockquote>
      ) : null}

      {shots.length > 0 ? (
        <>
          <div className="intakecard-shots">
            {shots.map((photo) =>
              urls[photo.id] ? (
                <a
                  key={photo.id}
                  href={urls[photo.id]}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={urls[photo.id]} alt={t("Sent by the customer")} />
                </a>
              ) : (
                <span key={photo.id} className="intakecard-shot-waiting" />
              ),
            )}
          </div>
          <div className="na-hint">
            {waiting === 0
              ? t("In CompanyCam, marked From customer.")
              : t(
                  "{n} of {total} in CompanyCam — the rest follow once the project is made.",
                  { n: shots.length - waiting, total: shots.length },
                )}
          </div>
        </>
      ) : null}
    </div>
  );
}
